import { useEffect } from 'react';

import { useLocation } from 'react-router-dom';

import { useGetProject } from '@/entities/project';

import { PATHS } from '@/shared/constants/routes/paths';
import { createModalStore, isAnyModalOpen, subscribeToSettledModalClose } from '@/shared/lib/modal';
import {
  PlanFeaturesTrigger,
  requestPlanFeaturesModal,
  usePlanFeaturesRequestStore,
} from '@/shared/lib/planFeaturesRequest';
import { usePlayerStore } from '@/shared/store-tmp/usePlayerStore';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { VideoTab } from '@/shared/ui/VideoPlayer/views/ProjectVideoPlayer/types';

import {
  MAX_PLAYBACK_STEP_SECONDS,
  PLAN_FEATURES_MAX_SHOWS_PER_SESSION,
  PLAN_FEATURES_SESSION_KEY,
  PLAN_FEATURES_WATCH_SECONDS,
} from '../lib/constants';
import { useIsOwnPaidTrial } from './useIsOwnPaidTrial';

export const usePlanFeaturesModalStore = createModalStore<{ trigger?: PlanFeaturesTrigger }>({ trigger: undefined });

const SILENT_PATHS = [PATHS.pricing, PATHS.checkout].map((path) => `/${path}`);

const readShownTriggers = (): string[] => {
  try {
    const shown: unknown = JSON.parse(sessionStorage.getItem(PLAN_FEATURES_SESSION_KEY) ?? '[]');
    return Array.isArray(shown) ? shown : [];
  } catch {
    return [];
  }
};

export const usePlanFeaturesModalTrigger = () => {
  const isOwnPaidTrial = useIsOwnPaidTrial();
  const { pathname } = useLocation();

  useEffect(() => {
    let pending: null | PlanFeaturesTrigger = null;

    const tryOpen = (trigger: PlanFeaturesTrigger) => {
      pending = null;
      const shown = readShownTriggers();

      if (
        !isOwnPaidTrial ||
        SILENT_PATHS.some((path) => pathname.startsWith(path)) ||
        usePlanFeaturesModalStore.getState().isOpen ||
        shown.length >= PLAN_FEATURES_MAX_SHOWS_PER_SESSION ||
        shown.includes(trigger)
      ) {
        return;
      }
      if (isAnyModalOpen()) {
        pending = trigger;
        return;
      }

      sessionStorage.setItem(PLAN_FEATURES_SESSION_KEY, JSON.stringify([...shown, trigger]));
      usePlanFeaturesModalStore.getState().open({ trigger });
      MixpanelEvents.paidTrialPlanFeaturesShown({ trigger });
    };

    const unsubscribeRequest = usePlanFeaturesRequestStore.subscribe(({ trigger }) => trigger && tryOpen(trigger));
    const unsubscribeClose = subscribeToSettledModalClose(() => pending && tryOpen(pending));

    return () => {
      unsubscribeRequest();
      unsubscribeClose();
    };
  }, [isOwnPaidTrial, pathname]);
};

export const usePlanFeaturesPlaybackTrigger = () => {
  const { data: hasResult } = useGetProject((project) => project.translated_video_exists);

  useEffect(() => {
    if (!hasResult) return;

    let watchedSec = 0;

    return usePlayerStore.subscribe((state, previous) => {
      const step = state.currentTimeSec - previous.currentTimeSec;
      const isDubbedPlayback = state.isPlaying && state.activeVideoTab === VideoTab.DstVideo;

      if (!isDubbedPlayback || step <= 0 || step > MAX_PLAYBACK_STEP_SECONDS) return;

      watchedSec += step;

      if (watchedSec >= PLAN_FEATURES_WATCH_SECONDS && watchedSec - step < PLAN_FEATURES_WATCH_SECONDS) {
        requestPlanFeaturesModal('playback');
      }
    });
  }, [hasResult]);
};
